import {Box, Tab, Tabs, Typography} from '@mui/material';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer} from 'recharts';
import {AdminDiagramStyle} from './AdminDiagram.style';
import {IIncomes} from '@/utils/types/superAdmin.interface';
import {StatisticTerm} from '@/utils/enums';
import {currencyFormatter} from '@/utils/helpers/currency';
import {useMemo} from 'react';

interface IAdminDiagram {
  diagramData: IIncomes[];
  setStatisticsTerm: (term: StatisticTerm) => void;
  statisticsTerm: StatisticTerm;
}
const settingsTabs: string[] = ['Daily', 'Weekly', 'Monthly'];

const UTC = 'UTC';

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const formatDate = (iso: string, term: StatisticTerm, full = false): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  switch (term) {
    case StatisticTerm.DAILY:
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        ...(full && {year: 'numeric'}),
        timeZone: UTC,
      });
    case StatisticTerm.WEEKLY: {
      const end = new Date(date);
      end.setUTCDate(end.getUTCDate() + 6);
      const opts: Intl.DateTimeFormatOptions = {day: '2-digit', month: 'short', timeZone: UTC};
      return full
        ? `${date.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`
        : date.toLocaleDateString('en-US', opts);
    }
    case StatisticTerm.MONTHLY:
    default:
      return date.toLocaleDateString('en-US', {
        month: 'short',
        ...(full && {year: 'numeric'}),
        timeZone: UTC,
      });
  }
};

const formatXLabel = (label: string, term: StatisticTerm): string => {
  const date = new Date(label);
  // якщо бекенд уже віддає готовий текст ("Jan", "Week 3") — лишаємо як є
  if (Number.isNaN(date.getTime())) return label;

  switch (term) {
    case StatisticTerm.DAILY:
      return date.toLocaleDateString('en-US', {day: '2-digit', month: 'short'});
    case StatisticTerm.WEEKLY:
      return date.toLocaleDateString('en-US', {day: '2-digit', month: 'short'});
    case StatisticTerm.MONTHLY:
    default:
      return date.toLocaleDateString('en-US', {month: 'short'});
  }
};

const AdminDiagram = ({diagramData, setStatisticsTerm, statisticsTerm}: IAdminDiagram) => {
  const chartData = useMemo(
    () => diagramData.map((item) => ({...item, label: formatXLabel(item.date, statisticsTerm)})),
    [diagramData, statisticsTerm]
  );

  const changeTab = (newTab: string) => () => {
    setStatisticsTerm(newTab as StatisticTerm);
  };

  return (
    <Box sx={AdminDiagramStyle.mainWrapper}>
      <Box sx={AdminDiagramStyle.tabWrapper}>
        <Typography sx={AdminDiagramStyle.income}>Income</Typography>
        <Tabs value={statisticsTerm} sx={AdminDiagramStyle.tabs}>
          {settingsTabs.map((tab: string) => (
            <Tab
              onClick={changeTab(tab)}
              key={tab}
              value={tab}
              label={<Box>{tab}</Box>}
              sx={AdminDiagramStyle.tab}
              disableRipple
            />
          ))}
        </Tabs>
      </Box>
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={diagramData} barSize={30}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickFormatter={(iso: string) => formatDate(iso, statisticsTerm)}
          />
          <YAxis
            width={60}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v: number) => `$${compactFormatter.format(v)}`}
          />
          <Tooltip
            labelFormatter={(iso: string) => formatDate(iso, statisticsTerm, true)}
            formatter={(value: number) => [currencyFormatter.format(value), 'Income']}
          />
          <Bar dataKey="value" fill="#00BFFF" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AdminDiagram;
